import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Mail } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { submitContact } from "@/lib/api";

const INTERESTS = [
  { value: "strategic-review", label: "Strategic Review" },
  { value: "network-assessment", label: "Network Cost & Efficiency Assessment" },
  { value: "ai-advisory", label: "AI + Operations Intelligence Advisory" },
  { value: "executive-briefing", label: "Executive Briefing / Workshop" },
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    interest: "strategic-review",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Transmission received. Axiom-AI will respond within one business day.");
      setDone(true);
      setForm({
        name: "",
        email: "",
        company: "",
        role: "",
        interest: "strategic-review",
        message: "",
      });
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Navigation />
      <main className="pt-28 pb-24 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            data-testid="contact-back"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-axiom-muted hover:text-axiom-text transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Back to home
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-2 mb-5">
                <span className="num-label">DIRECT TRANSMISSION</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <h1
                data-testid="contact-headline"
                className="font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              >
                Request a <br />
                Strategic <br />
                <span className="text-axiom-accent">Review.</span>
              </h1>
              <p className="mt-6 text-axiom-muted text-base leading-relaxed max-w-md">
                For executives, operators, investors, and leadership teams who
                need a precise read on where cost is moving inside their
                network — and what to do about it.
              </p>

              <div className="mt-10 space-y-5 border-l border-white/10 pl-5">
                <div>
                  <div className="num-label">RESPONSE TIME</div>
                  <p className="text-sm text-axiom-text mt-1">
                    Within one business day.
                  </p>
                </div>
                <div>
                  <div className="num-label">FORMAT</div>
                  <p className="text-sm text-axiom-text mt-1">
                    Confidential 30-minute scoping conversation.
                  </p>
                </div>
                <div>
                  <div className="num-label">OUTCOME</div>
                  <p className="text-sm text-axiom-text mt-1">
                    A clear next step. No template decks.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="axiom-card p-6 md:p-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Name" required>
                    <input
                      data-testid="contact-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className={inputClass}
                      placeholder="Jane Operator"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className={inputClass}
                      placeholder="jane@company.com"
                    />
                  </Field>
                  <Field label="Company">
                    <input
                      data-testid="contact-company"
                      type="text"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      className={inputClass}
                      placeholder="Acme Logistics"
                    />
                  </Field>
                  <Field label="Role">
                    <input
                      data-testid="contact-role"
                      type="text"
                      value={form.role}
                      onChange={(e) => update("role", e.target.value)}
                      className={inputClass}
                      placeholder="VP Supply Chain"
                    />
                  </Field>
                </div>

                <div className="mt-5">
                  <div className="num-label mb-2">AREA OF INTEREST</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {INTERESTS.map((i) => (
                      <button
                        type="button"
                        key={i.value}
                        data-testid={`contact-interest-${i.value}`}
                        onClick={() => update("interest", i.value)}
                        className={`text-left text-sm px-4 py-3 border transition-all ${
                          form.interest === i.value
                            ? "border-axiom-accent bg-axiom-accent/5 text-axiom-text"
                            : "border-white/10 text-axiom-muted hover:border-white/25 hover:text-axiom-text"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`signal-dot ${
                              form.interest === i.value ? "animate-pulse-signal" : "opacity-30"
                            }`}
                            style={{ width: 6, height: 6 }}
                          />
                          {i.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <Field label="What is the situation?" required>
                    <textarea
                      data-testid="contact-message"
                      rows={5}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      className={`${inputClass} resize-none`}
                      placeholder="A short description of what you're seeing — service, cost, dwell, forecast, or visibility patterns..."
                    />
                  </Field>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="mt-6 btn-axiom w-full justify-center disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Transmitting...
                    </>
                  ) : (
                    <>
                      <Mail size={16} /> Send transmission
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>

                {done && (
                  <p
                    className="mt-4 text-sm text-axiom-accent font-mono text-center"
                    data-testid="contact-success"
                  >
                    ✓ TRANSMISSION RECEIVED · AXIOM WILL RESPOND WITHIN 1 BUSINESS DAY
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const inputClass =
  "w-full bg-axiom-bg border border-white/10 px-4 py-3 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none transition-colors font-sans";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="num-label">
        {label} {required && <span className="text-axiom-accent">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
